import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Filter } from "lucide-react";

interface ColumnFilterHeaderProps {
  label: string;
  values: string[]; // distinct values for this column, pre-sorted
  selected: Set<string> | null; // null = no filter active (show all)
  onChange: (next: Set<string> | null) => void;
  width?: number;
  title?: string;
}

const ColumnFilterHeader: React.FC<ColumnFilterHeaderProps> = ({ label, values, selected, onChange, width, title }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const isActive = selected !== null;
  const visibleValues = useMemo(
    () => values.filter((v) => v.toLowerCase().includes(search.toLowerCase())),
    [values, search]
  );

  const isChecked = (v: string) => selected === null || selected.has(v);
  const allChecked = selected === null;

  const toggleValue = (v: string) => {
    const base = selected === null ? new Set(values) : new Set(selected);
    if (base.has(v)) base.delete(v);
    else base.add(v);
    onChange(base.size === values.length ? null : base);
  };

  const toggleAll = () => {
    onChange(allChecked ? new Set() : null);
  };

  return (
    <th className="sticky top-0 z-10 border border-gray-700 px-1.5 py-1 text-left bg-gray-800" style={{ width }} title={title}>
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide">{label}</span>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`p-0.5 rounded hover:bg-gray-700 ${isActive ? "text-yellow-400" : "text-gray-500"}`}
              title={`Filter ${label}`}
            >
              <Filter className="w-3 h-3" fill={isActive ? "currentColor" : "none"} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2 bg-gray-900 border-gray-700" align="start">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="h-7 text-xs mb-2"
            />
            <label className="flex items-center gap-2 text-xs text-gray-200 cursor-pointer pb-1 mb-1 border-b border-gray-700">
              <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
              <span className="font-medium">(Select all)</span>
            </label>
            <div className="max-h-52 overflow-y-auto space-y-1">
              {visibleValues.map((v) => (
                <label key={v} className="flex items-center gap-2 text-xs text-gray-200 cursor-pointer">
                  <Checkbox checked={isChecked(v)} onCheckedChange={() => toggleValue(v)} />
                  <span className="truncate">{v}</span>
                </label>
              ))}
              {visibleValues.length === 0 && <p className="text-[10px] text-gray-500 px-1">No matches</p>}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </th>
  );
};

export default ColumnFilterHeader;
